import React, { useCallback, useEffect, useState } from "react";
import { StoryBibleMenuContainer } from "./styles";
import SVG from "react-inlinesvg";
import { useStoryBibleStore } from "../../../../stores/storyBibleStore";
import { motion, AnimatePresence } from "framer-motion";
import classNames from "classnames";
import { useSudowriteStore } from "../../../../stores/sudowriteStore";
import { useProjectStore } from "../../../../stores/projectStore";
import { useSeriesStore } from "../../../../stores/seriesStore";
import { sendAppEvent } from "../../../../Utils";
import Toggle from "../../../Toggle";
import { EXPAND_STORY_BIBLE_MENU_EVENT } from "../../../PersistentChat/mentions/navigateToEntityMention";
import { suppressCommentSyncForStoryBibleNav } from "./storyBibleScrollSuppression";

const STORY_BIBLE_NAV_SECTIONS_COLLAPSED_KEY = "storyBibleNavSectionsCollapsed";

const readStoredNavSectionsCollapsed = () => {
  const stored = localStorage.getItem(STORY_BIBLE_NAV_SECTIONS_COLLAPSED_KEY);
  if (stored === null) return null;
  return stored === "true";
};

const sections = {
  seed: {
    label: "Braindump",
  },
  genre: {
    label: "Genre",
  },
  style: {
    label: "Style",
  },
  synopsis: {
    label: "Synopsis",
  },
  characters: {
    label: "Characters",
  },
  worldbuilding: {
    label: "Worldbuilding",
  },
  outline: {
    label: "Outline",
  },
};

const projectSections = {
  seed: sections.seed,
  genre: sections.genre,
  style: sections.style,
  synopsis: sections.synopsis,
};

const seriesSections = {
  characters: sections.characters,
  worldbuilding: sections.worldbuilding,
  outline: {
    label: "Outlines",
  },
};

const getSelectedOrLoadedDocumentId = () => {
  const { selectedDocumentId, document } = useProjectStore.getState();
  return selectedDocumentId ?? document?.id ?? null;
};

const scrollWithinCenterColumn = (element) => {
  const container = element.closest(".editor-stack");
  if (!container) return;

  // scrollIntoView also moves overflowing ancestors, which can clip the app toolbar.
  const top = container.scrollTop + element.getBoundingClientRect().top - container.getBoundingClientRect().top - container.clientTop;
  container.scrollTo({ top, behavior: "smooth" });
};

export const StoryBibleMenu = React.forwardRef(({ collapse, setIsStoryBibleCollapsed }, ref) => {
  const isStoryBibleActive = useStoryBibleStore((state) => state.isActive);
  const setStoryBibleIsActive = useStoryBibleStore((state) => state.setIsActive);
  const elementRefs = useStoryBibleStore((state) => state.elementRefs);
  const sectionInView = useStoryBibleStore((state) => state.sectionInView);
  const getSceneForDocument = useStoryBibleStore((state) => state.getSceneForDocument);
  const userData = useSudowriteStore((state) => state.userData);
  const project = useProjectStore((state) => state.project);
  const currentDocumentId = useProjectStore((state) => state.document?.id);
  const seriesSectionRef = useSeriesStore((state) => state.seriesSectionRef);
  const [isActive, setIsActive] = useState(isStoryBibleActive);
  const [userSectionsCollapsed, setUserSectionsCollapsed] = useState(readStoredNavSectionsCollapsed);
  const importBlocksStoryBible = project?.importStep && project.importStep !== "CANCELLED";
  const sectionsCollapsed = userSectionsCollapsed ?? collapse;
  const areSectionsExpanded = isActive && !sectionsCollapsed;

  const expandNavSections = useCallback(() => {
    setUserSectionsCollapsed(false);
    localStorage.setItem(STORY_BIBLE_NAV_SECTIONS_COLLAPSED_KEY, "false");
    setIsStoryBibleCollapsed(false);
  }, [setIsStoryBibleCollapsed]);

  const toggleNavSectionsCollapsed = useCallback(() => {
    const nextCollapsed = !sectionsCollapsed;
    setUserSectionsCollapsed(nextCollapsed);
    localStorage.setItem(STORY_BIBLE_NAV_SECTIONS_COLLAPSED_KEY, String(nextCollapsed));
    if (!nextCollapsed) {
      setIsStoryBibleCollapsed(false);
    }
  }, [sectionsCollapsed, setIsStoryBibleCollapsed]);

  // Track active scroll timeouts and animation frames for cleanup
  const scrollTimeoutRef = React.useRef(null);
  const animationFrameRef = React.useRef(null);

  const hasGeneratedProse = userData?.data?.onboarding?.storyBible?.hasGenerated?.includes("button-container-generate-prose");
  const sectionsToUse = project.seriesId ? projectSections : sections;

  const handleScroll = (key) => {
    // Cancel any ongoing scroll to prevent race conditions
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Suppress midline comment-card sync so programmatic scroll is uninterrupted
    suppressCommentSyncForStoryBibleNav();

    // Special case: if clicking Outline, try to scroll to the linked chapter
    if (key === "outline") {
      const documentIdAtStart = currentDocumentId;
      const isCurrentOutlineTarget = () => getSelectedOrLoadedDocumentId() === documentIdAtStart;
      const scene = documentIdAtStart && getSceneForDocument(documentIdAtStart);
      const chapterId = scene?.itemId;
      if (chapterId && isCurrentOutlineTarget()) {
        const chapterEl = document.getElementById(`story-bible-outline-chapter-${chapterId}`);
        if (chapterEl) {
          scrollWithinCenterColumn(chapterEl);
          return;
        }
        // Chapter element not mounted yet — scroll to section first, then poll for it
        const sectionRef = elementRefs?.["outline"]?.current;
        if (sectionRef) {
          scrollWithinCenterColumn(sectionRef);
          let pollAttempts = 0;
          const maxPollAttempts = 20;
          const pollForChapter = () => {
            suppressCommentSyncForStoryBibleNav();
            if (!isCurrentOutlineTarget()) {
              scrollTimeoutRef.current = null;
              return;
            }
            pollAttempts++;
            const el = document.getElementById(`story-bible-outline-chapter-${chapterId}`);
            if (el) {
              scrollWithinCenterColumn(el);
              scrollTimeoutRef.current = null;
            } else if (pollAttempts < maxPollAttempts) {
              scrollTimeoutRef.current = setTimeout(pollForChapter, 200);
            } else {
              // Chapter never appeared — ensure section top is stable
              scrollWithinCenterColumn(sectionRef);
              scrollTimeoutRef.current = null;
            }
          };
          scrollTimeoutRef.current = setTimeout(pollForChapter, 200);
          return;
        }
      }
    }

    const ref = elementRefs?.[key]?.current;
    if (!ref) return;

    // For lazy-loaded content, we need to keep scrolling until position stabilizes
    let lastScrollTop = -1;
    let attempts = 0;
    const maxAttempts = 20; // Limit to prevent infinite loops

    const scrollUntilStable = () => {
      suppressCommentSyncForStoryBibleNav();
      const element = elementRefs[key]?.current;
      if (!element || attempts >= maxAttempts) return;

      const currentScrollTop = element.offsetTop;

      // Scroll to the element
      scrollWithinCenterColumn(element);

      attempts++;

      // If the position has changed since last check, content is still loading
      // Keep checking until position stabilizes
      if (lastScrollTop !== currentScrollTop) {
        lastScrollTop = currentScrollTop;
        scrollTimeoutRef.current = setTimeout(scrollUntilStable, 300);
      } else {
        // Position stabilized - focus the section for screen readers
        element.focus({ preventScroll: true });
        scrollTimeoutRef.current = null;
      }
    };

    // Start the polling scroll
    animationFrameRef.current = requestAnimationFrame(scrollUntilStable);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Avoid toggling on and off story bible menu when loading isStoryBibleActive
  useEffect(() => {
    if (isStoryBibleActive !== null) {
      setIsActive(isStoryBibleActive);
    }
  }, [isStoryBibleActive]);

  useEffect(() => {
    const onExpandStoryBibleMenu = () => {
      expandNavSections();
    };
    window.addEventListener(EXPAND_STORY_BIBLE_MENU_EVENT, onExpandStoryBibleMenu);
    return () => window.removeEventListener(EXPAND_STORY_BIBLE_MENU_EVENT, onExpandStoryBibleMenu);
  }, [expandNavSections]);

  return (
    <StoryBibleMenuContainer
      className={classNames("story-bible-menu", { "sb-in-view": !!sectionInView, "is-active": isActive })}
      ref={ref}
    >
      <div className={classNames("inner-container", { collapse: !isActive || sectionsCollapsed })}>
        <div className="header">
          <div className="heading-row">
            {isActive && (
              <button
                type="button"
                className="sections-chevron use-component-styles"
                onClick={toggleNavSectionsCollapsed}
                aria-expanded={areSectionsExpanded}
                aria-controls="story-bible-menu-sections"
                aria-label={areSectionsExpanded ? "Collapse Story Bible sections" : "Expand Story Bible sections"}
              >
                <SVG
                  src="/assets/chevron-right.svg"
                  className={classNames("chevron-icon", { expanded: areSectionsExpanded })}
                  aria-hidden="true"
                />
              </button>
            )}
            <button
              className={classNames("heading", { highlight: !hasGeneratedProse, series: !!project.seriesId })}
              onClick={(e) => handleScroll("top")}
              aria-hidden="true"
              tabIndex={-1}
            >
              <div className="icon">
                <SVG
                  width={24}
                  height={24}
                  src="/assets/story-bible/story-bible-square.svg"
                  className="trash"
                />
              </div>
              <div className="title">Story Bible</div>
            </button>
          </div>
          <div className="toggle">
            <Toggle
              aria-label={
                importBlocksStoryBible
                  ? "Story Bible (unavailable while importing)"
                  : isActive
                    ? "Deactivate Story Bible"
                    : "Activate Story Bible"
              }
              checked={!!isActive}
              disabled={importBlocksStoryBible}
              title={importBlocksStoryBible ? "Unavailable while importing" : undefined}
              set={(checked) => {
                if (importBlocksStoryBible) return;
                sendAppEvent(`StoryBible - ${checked ? "Opened" : "Closed"}`);
                setStoryBibleIsActive(checked);
              }}
            />
          </div>
        </div>
        <AnimatePresence initial={false}>
          {!hasGeneratedProse && !isActive && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              layout={"position"}
              transition={{ duration: 0.75, ease: "easeIn" }}
              className="subheader"
            >
              Get better results by telling Sudowrite about your Characters, Outline, and more.
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence initial={false}>
          {isActive && (
            <motion.nav
              className={classNames("sections", { collapse: sectionsCollapsed })}
              aria-label="Story Bible sections"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              layout={"position"}
              transition={{ duration: 0.75, ease: "easeIn" }}
            >
              <ul id="story-bible-menu-sections">
                {Object.entries(sectionsToUse).map(([key, value]) => {
                  return (
                    <li
                      key={key}
                      className={classNames("section", { "in-view": key === sectionInView, series: project.seriesId })}
                    >
                      <a
                        href={`#story-bible-${key}`}
                        onClick={(e) => {
                          e.preventDefault();
                          handleScroll(key);
                        }}
                      >
                        <div className="section-icon">
                          <SVG
                            width={18}
                            height={18}
                            src={`/assets/story-bible/${key}-icon.svg`}
                          />
                        </div>
                        <div className="section-title">{value.label}</div>
                      </a>
                    </li>
                  );
                })}
                {project.seriesId && (
                  <li>
                    <div className="section-type-separator"></div>
                    <div
                      className="section-heading"
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        suppressCommentSyncForStoryBibleNav();
                        seriesSectionRef?.current?.scrollIntoView({ behavior: "smooth" });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          suppressCommentSyncForStoryBibleNav();
                          seriesSectionRef?.current?.scrollIntoView({ behavior: "smooth" });
                        }
                      }}
                    >
                      <SVG
                        width={20}
                        height={20}
                        src="/assets/series.svg"
                        className="trash"
                        aria-hidden="true"
                      />
                      <div className="title">Series</div>
                    </div>
                    {Object.entries(seriesSections).map(([key, value]) => {
                      return (
                        <div
                          key={key}
                          className={classNames("section", { "in-view": key === sectionInView, series: true })}
                        >
                          <a
                            href={`#story-bible-${key}`}
                            onClick={(e) => {
                              e.preventDefault();
                              handleScroll(key);
                            }}
                          >
                            <div className="section-icon">
                              <SVG
                                width={18}
                                height={18}
                                src={`/assets/story-bible/${key}-icon.svg`}
                              />
                            </div>
                            <div className="section-title">{value.label}</div>
                          </a>
                        </div>
                      );
                    })}
                  </li>
                )}
              </ul>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </StoryBibleMenuContainer>
  );
});
